/**
 * LRU-cache for dekodede bitmaps (reise-replay, eng-review D12 «rullerende
 * dekoding»): alle blobs preloades (nettverks-garanti, D2), men dekodede
 * ImageBitmaps holdes kun for et vindu — eldste lukkes (close()) ved evict så
 * minnetaket er uavhengig av reiselengde. Generisk over «Closable» så testene
 * slipper ekte ImageBitmap (finnes ikke i happy-dom).
 */

export interface Closable { close(): void }

export class BitmapLru<T extends Closable> {
  private map = new Map<string, T>();

  constructor(private readonly capacity: number) {
    if (!Number.isFinite(capacity) || capacity < 1) {
      throw new Error(`BitmapLru: ugyldig kapasitet ${capacity}`);
    }
  }

  /** Hent uten å endre LRU-orden brukes ikke — get markerer alltid som nylig brukt. */
  get(key: string): T | undefined {
    const hit = this.map.get(key);
    if (hit !== undefined) {
      // Re-insert for å flytte til «nyest»-enden av Map-iterasjonsorden.
      this.map.delete(key);
      this.map.set(key, hit);
    }
    return hit;
  }

  has(key: string): boolean {
    return this.map.has(key);
  }

  /** Sett inn; evicter (og close()-er) eldste ved kapasitetsbrudd. */
  set(key: string, value: T): void {
    const existing = this.map.get(key);
    if (existing !== undefined && existing !== value) existing.close();
    this.map.delete(key);
    this.map.set(key, value);
    while (this.map.size > this.capacity) {
      const oldestKey = this.map.keys().next().value as string;
      const oldest = this.map.get(oldestKey)!;
      this.map.delete(oldestKey);
      oldest.close();
    }
  }

  get size(): number {
    return this.map.size;
  }

  /** Fjern og close() én oppføring (f.eks. når brukeren sletter et medieinnslag). */
  delete(key: string): void {
    const v = this.map.get(key);
    if (v !== undefined) { v.close(); this.map.delete(key); }
  }

  /** Lukk alt (unmount/avbrutt eksport) — cachen kan gjenbrukes etterpå. */
  clear(): void {
    for (const v of this.map.values()) v.close();
    this.map.clear();
  }
}
